import { ApiError, NetworkError, describeError } from "../../api/client";

/** The three lifecycle-changing writes this tab exposes (task 9.8). */
export type ConsequentialAction = "schedule" | "dispatch" | "cancel";

/**
 * Confirmation-dialog copy naming the campaign and the specific consequence
 * (redesign-panel-hubspot-mailgun, PR 9, task 9.8; design.md §5.2:
 * "schedule/dispatch/cancel require a focused confirmation dialog that names
 * the campaign and consequence"). Extracted as a pure function
 * (Extract-Before-Mock) so the copy is testable independently of whatever
 * renders it.
 *
 * Issue #166: the caller used to be `window.confirm()` directly — the SAME
 * native mechanism the unsaved-changes blocker still uses
 * (`state/routeState.ts#useUnsavedChangesBlocker`). But Chromium silently
 * auto-suppresses further `confirm()`/`alert()` calls after several have
 * appeared in the same tab, resolving them to `false` with zero visible
 * feedback — a suppressed dialog was indistinguishable from a real bug.
 * `CampaignSummaryTab` now renders this copy inside its own in-app
 * `ConfirmDialog` (`components/ConfirmDialog.tsx`) instead — immune to that
 * browser-level suppression, and, unlike a native `<dialog>`/`showModal()`,
 * it needs no `showModal` support in this project's jsdom 25.0.1 test
 * toolchain. This repo's other `window.confirm` call sites (Templates
 * soft-delete/attachment-delete) are unaffected and out of this issue's
 * scope.
 */
export function buildConsequentialConfirmation(action: ConsequentialAction, campaignName: string): string {
  switch (action) {
    case "schedule":
      return `¿Programar la campaña "${campaignName}"? Esto activará el envío automático en la fecha indicada.`;
    case "dispatch":
      return `¿Despachar la campaña "${campaignName}" ahora? Esto inicia el envío inmediato a los contactos del segmento asignado.`;
    case "cancel":
      return `¿Cancelar la campaña "${campaignName}"? Esta acción no se puede deshacer y detiene cualquier envío programado.`;
  }
}

/**
 * Confirmation copy for "Programar todo el lote" (odd/tasks/
 * campaign-batch-bulk-schedule.md task 2, `CampaignListScreen`'s
 * batch-header row) — same in-app `ConfirmDialog` convention as
 * `buildConsequentialConfirmation` above (issue #166: never
 * `window.confirm()`), kept as its own function rather than folded into
 * `ConsequentialAction` since it names a COUNT of sibling campaigns, not one
 * named campaign — a different shape than every action in that union.
 *
 * Codex round 1 fix (P1 #2): `visiblePageEligibleCount` is deliberately NEVER
 * asserted as the total scope of the action. `pendingBatchSchedule.lotes`
 * (the caller) only ever contains the CURRENTLY LOADED PAGE
 * (`buildCampaignDisplayRows` is fed the current page only — default size
 * 20, max 50, see `./filters.ts`), while a real batch can have up to 100
 * siblings (`AdminCampaignBatchValidationPolicy.MaxLotes`) and the backend
 * endpoint (`AdminCampaignRepository.ScheduleBatchAsync`) schedules EVERY
 * `'borrador'` database sibling regardless of what was loaded client-side.
 * The old copy stated the visible-page count as if it were the total
 * ("se van a programar N campañas en borrador") — an operator seeing "20 en
 * borrador" and confirming could actually trigger 100. This copy states
 * whole-batch scope explicitly ("TODAS ... de este lote") and uses the
 * visible-page count only as a disclosed floor, never a ceiling.
 */
export function buildBatchScheduleConfirmation(visiblePageEligibleCount: number): string {
  return `¿Programar todo el lote? Se van a programar TODAS las campañas en borrador de este lote con la fecha ya asignada a cada una (puede haber más de las ${visiblePageEligibleCount} visibles en esta página).`;
}

/**
 * Safe-recovery error copy for a rejected schedule/dispatch/cancel call
 * (task 9.8; design.md §5.2: "failure preserves inputs and route context and
 * offers only safe recovery: ... authenticated unauthorized writes/role
 * failures map to permission-denied 403 ... 409, validation, and transient
 * failures remain distinct"). `401` is handled globally by `apiFetch`
 * (redirects to login before this ever runs) — not this function's concern.
 */
export function describeConsequentialActionError(error: unknown): string {
  if (error instanceof ApiError && error.kind === "forbidden") {
    return "No tiene permisos para realizar esta acción.";
  }

  if (error instanceof NetworkError) {
    return error.message;
  }

  return describeError(error, "No se pudo completar la acción. Intente nuevamente.");
}
